// util/interface-list-diff-display-util.ts
import { ReviewDiffCellDetail } from "../services/if-definitions";
import { normalizeDiffValue } from "./diff-util";
import { Option } from "./option";

type GeneralCodeStateKey =
  | "commonConditionFlagType"
  | "securityContentsType"
  | "sendDatalakeType"
  | "fileExtensionType"
  | "interfaceRequirementType"
  | "edhNotificationType"
  | "edhPrtclType"
  | "ptnrType"
  | "chnTypeType"
  | "fileTypeType"
  | "ifCycleType"
  | "snapshotIncrementalDeltaType"
  | "dataScopeType"
  | "encriptionType"
  | "securityInformationLevelType"
  | "retentionPeriodDays"
  | "ediPollingAdaptorType"
  | "interruptType";

type GeneralCodeLike = Record<string, Option[] | undefined>;

const COMMON_FLAG_FIELDS = new Set([
  "metaOnlyFlag",
  "permitNoFile",
  "partnerInitFlg",
  "contentsAccessibleFlag",
  "useEtlTransceiver",
  "useConverter",
  "concurrentFlag",
  "differentLayoutsForInputAndOutput",
  "securityInformationTransferFlagValue",
  "autoPendingFlag",
  "autoRetryFlg",
  "includeDtd",
  "notifyFlag",
  "receiveFlg",
  "sendFlg",
]);

const SECURITY_CONTENT_FIELDS = new Set([
  "nameValue",
  "postcodeValue",
  "addressValue",
  "phoneValue",
  "emailValue",
  "genderValue",
  "dateOfBirthValue",
  "membershipIdEtcValue",
  "employeeIdValue",
  "governmentIssuedIdValue",
  "bankAccountInfoValue",
  "paymentCardInfoValue",
  "orderInfoValue",
  "purchaseInfoValue",
  "systemInfoValue",
  "deviceInfoValue",
  "geoLocationValue",
  "ipAddressValue",
  "sizeInfoValue",
  "healthInfoValue",
]);

const resolveGeneralCodeStateKey = (
  diffPath: string | undefined,
): GeneralCodeStateKey | undefined => {
  if (!diffPath) return undefined;

  const fieldName = diffPath.split(".").pop() ?? "";

  if (COMMON_FLAG_FIELDS.has(fieldName)) {
    return "commonConditionFlagType";
  }

  if (SECURITY_CONTENT_FIELDS.has(fieldName)) {
    return "securityContentsType";
  }

  if (fieldName === "interfaceRequirementType")
    return "interfaceRequirementType";
  if (fieldName === "partnerType") return "ptnrType";
  if (fieldName === "chainTypeCode") return "chnTypeType";
  if (fieldName === "fileTypeCode") return "fileTypeType";
  if (fieldName === "notificationType") return "edhNotificationType";
  if (fieldName === "protocol") return "edhPrtclType";
  if (fieldName === "fileExtension") return "fileExtensionType";
  if (fieldName === "ifCycle") return "ifCycleType";
  if (fieldName === "snapshotIncrementalDelta")
    return "snapshotIncrementalDeltaType";
  if (fieldName === "dataScopePerInterfaceId") return "dataScopeType";
  if (fieldName === "dataScopePerInterfaceFile") return "dataScopeType";
  if (fieldName === "securityInformationLevel")
    return "securityInformationLevelType";
  if (fieldName === "inboundEncryptionTypeValue") return "encriptionType";
  if (fieldName === "outboundEncryptionTypeValue") return "encriptionType";
  if (fieldName === "retentionPeriodValue") return "retentionPeriodDays";
  if (fieldName === "sendDatalakeType") return "sendDatalakeType";
  if (fieldName === "pollingAdaptorTypeCode") return "ediPollingAdaptorType";
  if (fieldName === "interruptType" || fieldName.endsWith("InterruptType")) {
    return "interruptType";
  }

  return undefined;
};

const resolveLabel = (
  options: Option[] | undefined,
  value: ReviewDiffCellDetail["before"],
): string => {
  const normalizedValue = normalizeDiffValue(value);

  if (!options || normalizedValue === "") {
    return normalizedValue;
  }

  return (
    options.find((option) => String(option.value) === normalizedValue)?.label ??
    normalizedValue
  );
};

export const formatInterfaceListDiffForDisplay = (
  diff: ReviewDiffCellDetail | undefined,
  diffPath: string | undefined,
  interfaceListGeneralCodeState: GeneralCodeLike,
): ReviewDiffCellDetail | undefined => {
  if (!diff) return undefined;

  const generalCodeStateKey = resolveGeneralCodeStateKey(diffPath);
  if (!generalCodeStateKey) return diff;

  const options = interfaceListGeneralCodeState[generalCodeStateKey];

  return {
    ...diff,
    before: resolveLabel(options, diff.before),
    after: resolveLabel(options, diff.after),
  };
};
